import { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Textarea,
  Icon,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircle, Plus } from 'lucide-react';
import { Rock, Issue, User } from '../../types';

interface RockDiscussionPanelProps {
  rock: Rock;
  owner: User | undefined;
  issues: Issue[];
  onAddNote: (note: string) => Promise<void>;
  onUpdateStatus: (newStatus: string) => Promise<void>;
  onCreateIssue: (issueData: { name: string; description: string; priority?: 'low' | 'medium' | 'high' }) => Promise<void>;
  onResolveIssue: (issueId: string) => Promise<void>;
  onMarkReviewed: () => Promise<void>;
  isReviewed: boolean;
}

export function RockDiscussionPanel({
  rock,
  owner,
  issues,
  onAddNote,
  onUpdateStatus,
  onCreateIssue,
  onResolveIssue,
  onMarkReviewed,
  isReviewed,
}: RockDiscussionPanelProps) {
  const [loading, setLoading] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box bg={bgColor} borderWidth={1} borderColor={borderColor} borderRadius="md" p={6}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="semibold">
              {rock.rock}
            </Text>
            {owner && <Text fontSize="sm" color="gray.600">{owner.name}</Text>}
          </VStack>
          {isReviewed && (
            <Badge colorScheme="green">
              <Icon as={CheckCircle} w={3} h={3} mr={1} />
              Reviewed
            </Badge>
          )}
        </HStack>

        {issues.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Related Issues</Text>
            <VStack align="stretch" spacing={2}>
              {issues.map((issue) => (
                <Box key={issue.id} p={2} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="medium">{issue.name}</Text>
                  <Text fontSize="xs" color="gray.600">{issue.description}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {!isReviewed && (
          <Button
            leftIcon={<Icon as={CheckCircle} w={4} h={4} />}
            colorScheme="green"
            size="sm"
            onClick={onMarkReviewed}
            isLoading={loading}
          >
            Mark as Reviewed
          </Button>
        )}
      </VStack>
    </Box>
  );
}

